import { Box, Checkbox, Drawer, Grid2, Paper, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { ValidatorForm } from "react-material-ui-form-validator";
import { useParams } from "react-router-dom";
import { TicketTypeEnum } from "../../_common/enum/ticketType.enum";
import EnumSelect from "../../_components/autocomplete/enum.select";
import { StandardButton } from "../../_components/button/primary.button";
import { DrawerHeader } from "../../_components/drawer/header.drawer";
import { StandardTextValidator } from "../../_components/textValidator/standardTextValidator";
import { toastr } from "../../_components/toastr/toastr";
import { handleErrorResponse } from "../../_helpers/handleResponse.helper";
import { ticketActionListService } from "../../_services/ticketActionList.service";
import { dropdownService } from "../../_services/dropdown.service";
import { DataTypeEnum } from "../../_common/enum/dataType.enum";
import { StandardList } from "../../_components/list/standardList.component";
import { StandardListItem } from "../../_components/list/standardListItem.component";


export const TicketActionListManageDrawer = (params: { isOpen?: boolean, onSave(): void, onClose(): void }) => {

    const resetData = () => ({});

    const { id } = useParams<{ id: string }>();

    const [data, setData] = useState<any>(resetData);
    const [isLoading, setIsLoading] = useState(false);
    const [saving, setIsSaving] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [ticketActionListTypes, setTicketActionListTypes] = useState<any[]>([]);
    const [selectedActionTypeIds, setSelectedActionTypeIds] = useState<number[]>([]);

    const orderedTypes = [...ticketActionListTypes]
        .map((typeItem) => {
            const matchingItem = data.ticketActionListItems?.find((item: any) => item.actionTypeId === typeItem.id);
            return {
                ...typeItem,
                itemOrder: matchingItem?.itemOrder,
                isChecked: selectedActionTypeIds.includes(typeItem.id)
            };
        });

    // Separate ordered vs unordered
    const itemsWithOrder = orderedTypes
        .filter(item => typeof item.itemOrder === 'number')
        .sort((a, b) => a.itemOrder - b.itemOrder);

    const itemsWithoutOrder = orderedTypes
        .filter(item => typeof item.itemOrder !== 'number');

    // Final ordered array
    const displayItems = [...itemsWithOrder, ...itemsWithoutOrder];

    useEffect(() => {
        fetchTicketActionListTypes();

        if (id != null)
            fetchData();
        else
            setData(resetData);
    }, [id]);

    useEffect(() => {
        if (isOpen == false)
            setIsOpen(true);
    }, []);

    function fetchData() {
        if (!id) return;

        setIsLoading(true);
        return ticketActionListService
            .get(id)
            .then(
                (json) => {
                    setData(json);
                    const selectedIds = json.ticketActionListItems?.map((item: any) => item.actionTypeId) || [];
                    setSelectedActionTypeIds(selectedIds);
                }, handleErrorResponse
            )
            .finally(() => {
                setIsLoading(false);
            });
    }

    function fetchTicketActionListTypes() {
        return dropdownService.fetchData(DataTypeEnum.ActionTypes)
            .then(
                (json) => {
                    setTicketActionListTypes(json);
                }, handleErrorResponse
            );
    }

    function submit(e: any) {
        e.preventDefault();
        setIsSaving(true);

        const _data = {
            ...data,
            ticketActionListItems: selectedActionTypeIds.map((id) => {
                const type = ticketActionListTypes.find(t => t.id === id);
                const existingItem = data.ticketActionListItems?.find((item: any) => item.actionTypeId === id);

                const _item: any = {
                    id: existingItem?.id,
                    actionTypeId: id,
                    name: type?.name || "",
                    itemOrder: existingItem?.itemOrder ?? 0
                };

                // Only add id if editing
                if (existingItem?.id != null) {
                    _item.id = existingItem.id;
                }

                return _item;
            })
        };

        return ticketActionListService
            .submit(_data)
            .then(
                () => {
                    toastr.success("Saved successfully");
                    resetData();
                    params.onSave();
                }, handleErrorResponse
            )
            .finally(() => setIsSaving(false));
    }

    function disable() {
        if (!id) return;
        setIsLoading(true);
        return ticketActionListService
            .disable(id)
            .then(
                () => {
                    toastr.success("Disabled successfully");
                    params.onSave();
                }, handleErrorResponse
            )
            .finally(() => setIsLoading(false));
    }

    function handleChange(event: any) {
        setData((_data: any) => ({ ..._data, [event.target.name]: event.target.value }));
    }

    function handleCheckboxToggle(typeId: number) {
        setSelectedActionTypeIds((_data) =>
            _data.includes(typeId)
                ? _data.filter(id => id !== typeId)
                : [..._data, typeId]
        );
    };

    function handleItemOrderChange(actionTypeId: number, value: number) {
        setData((_data: any) => {
            const existing = _data.ticketActionListItems || [];
            const index = existing.findIndex((item: any) => item.actionTypeId === actionTypeId);

            const updatedItem = {
                ...(existing[index] || {}), actionTypeId, itemOrder: value
            };

            let itemList;
            if (index >= 0) {
                itemList = [...existing];
                itemList[index] = updatedItem;
            } else {
                itemList = [...existing, updatedItem];
            }

            return {
                ..._data, ticketActionListItems: itemList
            };
        });
    };

    return (
        <>
            <Drawer
                anchor={"right"}
                variant="persistent"
                open={isOpen}
                ModalProps={{
                    keepMounted: true
                }}>

                <DrawerHeader name={(!!id) ? "Update Ticket Action List" : "Create Ticket Action List"} onClose={params.onClose} />

                <ValidatorForm
                    onSubmit={submit}
                    noValidate>

                    <Grid2 container spacing={1} sx={{ "padding": "8px" }}>
                        <Grid2 size={{ xs: 12 }}>
                            <StandardTextValidator
                                key="name"
                                name="name"
                                label="Name"
                                required
                                fullWidth
                                validators={['required', 'minStringLength:1']}
                                errorMessages={['required field', "Field cannot be empty"]}
                                value={data.name}
                                onChange={handleChange}
                            />
                        </Grid2>

                        <Grid2 size={{ xs: 12 }}>
                            <Grid2 size={{ xs: 12 }}>
                                <EnumSelect
                                    key="ticketTypeId"
                                    enumObject={TicketTypeEnum}
                                    label="Select Ticket Type"
                                    name="ticketTypeId"
                                    value={data.ticketTypeId}
                                    onChange={handleChange}
                                    required
                                    fullWidth
                                />
                            </Grid2>
                        </Grid2>

                        <Grid2 size={{ xs: 12 }}>
                            <StandardTextValidator
                                key="accountCode"
                                name="accountCode"
                                label="Account Code"
                                fullWidth
                                value={data.accountCode}
                                onChange={handleChange}
                            />
                        </Grid2>

                        <Grid2 display="flex" justifyContent="flex-start">
                            <Typography variant="h6" mt={2 }> Select Actions</Typography>
                        </Grid2>

                        <StandardList isLoading={isLoading}>
                            {!isLoading && displayItems.map((typeItem: any) => {
                                const matchingItem = data.ticketActionListItems?.find(
                                    (item: any) => item.actionTypeId === typeItem.id
                                );

                                return (
                                    <StandardListItem
                                        primary={typeItem.name}
                                        secondary={
                                            <StandardTextValidator
                                                key={`itemOrder-${typeItem.id}`}
                                                name="itemOrder"
                                                label="Order"
                                                fullWidth
                                                type="number"
                                                value={matchingItem?.itemOrder ?? ''}
                                                onChange={(e: any) =>
                                                    handleItemOrderChange(typeItem.id, Number(e.target.value))
                                                }
                                                sx={{ width: 100 }}
                                            />
                                        }
                                        tertiary={
                                            <Checkbox
                                                checked={selectedActionTypeIds.includes(typeItem.id)}
                                                onChange={() => handleCheckboxToggle(typeItem.id)}
                                            />
                                        }
                                        key={typeItem.id}

                                    />

                                );
                                {/* <Grid2 key={typeItem.id}>
                                     <Paper
                                        elevation={1}
                                        sx={{
                                            p: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            borderRadius: 2,
                                            backgroundColor: '#f9f9f9',
                                            '&:hover': {
                                                backgroundColor: '#f1f1f1',
                                            },
                                        }}
                                    >
                                        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                                            <Typography variant="body1">{typeItem.name}</Typography>

                                            <Box display="flex" alignItems="center" gap={1}>
                                                <StandardTextValidator
                                                    key={`itemOrder-${typeItem.id}`}
                                                    name="itemOrder"
                                                    label="Order"
                                                    fullWidth
                                                    type="number"
                                                    value={matchingItem?.itemOrder ?? ''}
                                                    onChange={(e: any) =>
                                                        handleItemOrderChange(typeItem.id, Number(e.target.value))
                                                    }
                                                    sx={{ width: 100 }}
                                                />

                                                <Checkbox
                                                    checked={selectedActionTypeIds.includes(typeItem.id)}
                                                    onChange={() => handleCheckboxToggle(typeItem.id)}
                                                />
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Grid2>*/}
                            }
                            )}
                        </StandardList>





                        <Grid2 size={{ xs: 12 }}>
                            <Grid2 container spacing={1}>
                                <Grid2 size={{ xs: data.id == null ? 12 : data?.isActive === false ? 12 : 6 }}>
                                    <StandardButton
                                        saving={saving}
                                        loading={isLoading}
                                        icon="save"
                                        type="submit">
                                        {(data?.isActive === false) ? "Save and Reactivate" : "Save"}
                                    </StandardButton>
                                </Grid2>

                                {(!!id && data?.isActive === true) &&
                                    <Grid2 size={{ xs: 6 }}>
                                        <StandardButton
                                            saving={saving}
                                            loading={isLoading}
                                            icon="block"
                                            variant="error"
                                            onClick={disable}>
                                            Disable
                                        </StandardButton>
                                    </Grid2>
                                }
                            </Grid2>
                        </Grid2>
                    </Grid2>

                </ValidatorForm>
            </Drawer>
        </>

    );
};